import ResizeHandle from '@components/common/ResizeHandle'
import DocumentListViewer from '@components/dialogs/DocumentListViewer'
import { ExamSelectorDialog } from '@components/dialogs/ExamSelectorDialog'
import DocumentsList from '@components/documents/DocumentsList'
import PreferredExamsPopover from '@components/exam/PreferredExamsPopover'
import MedicalOrderForm from '@components/medical-order/MedicalOrderForm'
import OrderRecipientList from '@components/order/OrderRecipientList'
import OrderHistoryComponent from '@components/OrderHistoryComponent'
import PatientDataContainer from '@components/patient/PatientDataContainer'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Add } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Slide,
  Typography,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { Document, MedicalOrder } from '@services/api'
import { OrderDto, PatientStats } from '@services/dtos'
import {
  doAddMedicalOrder,
  doFindOrders,
  doGetOrderDocuments,
  doGetOrderHistory,
  setSelectedOrder as setReduxSelectedOrder,
} from '@state/reducers/orderReducer'
import React, { useEffect, useState } from 'react'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { RightTransition } from '../../../components/animations'
import {
  doGetMemberSituation,
  setMemberSituation,
} from '../../../state/reducers/billingReducer'
import { doGetMemberPreferences } from '../../../state/reducers/memberPreferencesReducer'
import { setPatientDetails } from '../../../state/reducers/patientsReducer'
import { setRecipients } from '../../../state/reducers/recipientReducer'
import Quotation from './Quotation/Quotation'
import {
  CommentsPopoverTrigger,
  CommentsPopover,
} from '@components/tables/components/CommentsPopover'
import { useCommentsPopover } from '@hooks/useCommentPopover'
import { doGetAllPatientStats } from '@state/thunks/patientsThunk'
import { enqueueSnackbar } from 'notistack'
import { Ambulance, ClipboardPlus, Rabbit, Wallet } from 'lucide-react'
import { TransportForm } from '../ambulance/TransportForm'
import { DialogTitle } from '@components/titles'
import { MissedAppointment } from './MissedAppointment'
import { EntityNameEnum } from '@utils/enums'
import UnpaidPatientInvoicesDialog from '@components/dialogs/UnpaidPatientInvoicesDialog'

type Props = {
  order?: OrderDto
  isOpen: boolean
  onClose: () => void
  isNested?: boolean
}

export const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export const SlideInPortal = ({
  isOpen,
  onClose,
  patientId,
  visitId,
}: {
  isOpen: boolean
  onClose: () => void
  patientId?: number
  visitId?: number
}) => {
  const dispatch = useAppDispatch()

  const handleClose = () => {
    dispatch(doFindOrders())
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '100vw',
        },
      }}
      fullWidth
      fullScreen
      TransitionComponent={RightTransition}
    >
      <div className="h-full overflow-y-auto relative">
        {patientId && (
          <PatientDataContainer
            patientId={patientId}
            visitId={visitId}
            containerClassName="max-h-full"
            onClose={onClose}
          />
        )}
      </div>
    </Dialog>
  )
}

const OrderDialog: React.FC<Props> = ({ order, isOpen, onClose, isNested }) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(setPatientDetails(null))
    }
  }, [])

  const handleClose = () => {
    dispatch(doFindOrders())
    dispatch(setRecipients([]))
    onClose()
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullScreen
        disableEscapeKeyDown
        TransitionComponent={Transition}
      >
        <DialogContent
          style={{ backgroundColor: '#F3F4F6' }}
          sx={{
            padding: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {order && (
            <OrderDetailsComponent
              order={order}
              isNested={isNested}
              onClose={onClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export const OrderDetailsComponent = ({
  order,
  editByDefault = false,
  isNested = false,
  onClose,
}: {
  order: OrderDto
  editByDefault?: boolean
  isNested?: boolean
  onClose?: () => void
}) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.auth.user)
  const orderHistory = useAppSelector((state) => state.order.orderHistory)
  const selectedOrder = useAppSelector((state) => state.order.selectedOrder)
  const effectiveOrder =
    selectedOrder && selectedOrder.id === order.id ? selectedOrder : order

  // --------------------------------------- State ---------------------------------------
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  )
  const [isExamSelectorVisible, setIsExamSelectorVisible] = useState(false)
  const [isPatientInfoOpen, setIsPatientInfoOpen] = useState(false)
  const [isEditingDocuments, setisEditingDocuments] = useState(false)
  const [examButtonAnchor, setExamButtonAnchor] = useState<HTMLElement | null>(
    null,
  )
  const [loadingExamId, setLoadingExamId] = useState<number | null>(null)
  const [patientStats, setPatientStats] = useState<PatientStats>()
  const [loading, setLoading] = useState(false)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
  const [isTransportDialogOpen, setIsTransportDialogOpen] = useState(false)
  const [isExamHistoryDialogOpen, setIsExamHistoryDialogOpen] = useState(false)
  const [isNoShowDialogOpen, setIsNoShowDialogOpen] = useState(false)
  const [quickPayDialogOpen, setQuickPayDialogOpen] = useState(false)

  const {
    commentDialogOpen,
    editingComment,
    handleOpenComments,
    handleCloseComments,
    setCommentDialogOpen,
    setEditingComment,
    commentsPopover,
  } = useCommentsPopover()

  const fetchPatientStats = async (patientId: number) => {
    setLoading(true)
    try {
      const stats = await dispatch(doGetAllPatientStats(patientId)).unwrap()
      setPatientStats(stats)
    } catch (e) {
      dispatch(
        enqueueSnackbar({
          message: 'Erreur lors de la récupération des statistiques',
          options: { variant: 'error' },
        }),
      )
    } finally {
      setLoading(false)
    }
  }

  // --------------------------------------- Hooks ---------------------------------------

  useEffect(() => {
    if (order?.attendingDoctor) {
      dispatch(doGetMemberSituation(order.attendingDoctor.id))
    }
  }, [order?.attendingDoctor])

  useEffect(() => {
    if (order) {
      if (order.attendingDoctor) {
        dispatch(doGetMemberSituation(order.attendingDoctor.id))
      } else {
        dispatch(setMemberSituation(null))
      }
    }
  }, [order])

  useEffect(() => {
    dispatch(setRecipients([]))
    dispatch(doGetMemberPreferences())
  }, [])

  useEffect(() => {
    if (order?.patient?.id) {
      fetchPatientStats(order?.patient?.id)
      dispatch(
        doGetOrderHistory({ patientId: order.patient.id, page: 0, limit: 5 }),
      )
    }
  }, [order?.patient?.id])

  useEffect(() => {
    if (order?.id) {
      setIsLoadingDocuments(true)
      dispatch(doGetOrderDocuments(order.id)).finally(() =>
        setIsLoadingDocuments(false),
      )
    }
  }, [order?.id])

  const isExamAlreadyInOrder = (examId: number) =>
    order?.medicalOrders?.some((mo) => mo.exam.id === examId) ?? false

  // --------------------------------------- Handlers ---------------------------------------
  const handleExamSelectionChange = (examIds: number[]) => {
    if (!order) {
      return
    }
    const duplicates = examIds.filter(isExamAlreadyInOrder)
    const toAdd = examIds.filter((id) => !isExamAlreadyInOrder(id))

    if (duplicates.length > 0) {
      dispatch(
        enqueueSnackbar({
          message: `${duplicates.length} examen(s) déjà présent(s) ont été ignoré(s)`,
          options: { variant: 'warning' },
        }),
      )
    }
    for (const exId of toAdd) {
      dispatch(doAddMedicalOrder({ examId: exId, orderId: order.id }))
    }
  }

  const handleSingleExamSelect = async (examId: number) => {
    if (!order) {
      return
    }
    if (isExamAlreadyInOrder(examId)) {
      dispatch(
        enqueueSnackbar({
          message: 'Cet examen est déjà ajouté à la demande',
          options: { variant: 'warning' },
        }),
      )
      return
    }
    setLoadingExamId(examId)
    try {
      await dispatch(
        doAddMedicalOrder({ examId: examId, orderId: order.id }),
      ).unwrap()
    } catch (error) {
      console.error('Failed to add exam:', error)
    } finally {
      setLoadingExamId(null)
    }
  }

  const onMedicalOrderRemoved = (mo: MedicalOrder) => {
    if (!order) {
      return
    }
    const newvalue = { ...order }
    newvalue.medicalOrders = newvalue.medicalOrders.filter(
      (medicalOrder) => medicalOrder.id !== mo.id,
    )
  }

  const handleOnFileUploaded = (doc: Document) => {
    if (!order) {
      return
    }
    setisEditingDocuments(false)
    const newDocuments = [...(order.documents ?? []), doc]
    const newOrder = { ...order, documents: newDocuments }
    dispatch(setReduxSelectedOrder(newOrder))
  }

  const handleDocumentDeleted = (doc: Document) => {
    if (!order) {
      return
    }
    const newOrder = {
      ...order,
      documents: (order.documents ?? []).filter((d) => d.id !== doc.id),
    }
    dispatch(setReduxSelectedOrder(newOrder))
  }

  const orderActionToolbar = (
    <>
      <Button
        variant="contained"
        onClick={(event) => setExamButtonAnchor(event.currentTarget)}
        size="small"
        sx={{
          height: 34,
          px: 1.5,
          borderRadius: '6px',
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          fontWeight: 700,
          textTransform: 'uppercase',
          bgcolor: '#7c3aed',
          boxShadow: '0 6px 14px rgba(124, 58, 237, 0.16)',
          whiteSpace: 'nowrap',
          '&:hover': {
            bgcolor: '#6d28d9',
            boxShadow: '0 8px 16px rgba(124, 58, 237, 0.2)',
          },
        }}
      >
        <Add sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }} />
        <span className="ml-1">Ajouter un examen</span>
      </Button>
      <div className="flex h-[34px] shrink-0 items-center justify-end gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 text-gray-700">
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' } }}
        >
          Commentaires
        </Typography>
        <CommentsPopoverTrigger
          order={order}
          handleOpenComments={handleOpenComments}
        />
      </div>
    </>
  )

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <PanelGroup direction="horizontal" style={{ height: '100%' }}>
        <Panel
          id="left-panel"
          collapsible
          order={1}
          defaultSize={25}
          minSize={15}
        >
          <div className="flex h-full flex-col overflow-hidden p-2">
            <div className="mb-2 shrink-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex min-w-0 items-start gap-3">
                <button
                  type="button"
                  onClick={() => setIsPatientInfoOpen(true)}
                  className="group relative shrink-0"
                  aria-label="Voir les détails du patient"
                >
                  <Avatar
                    sx={{
                      width: { xs: 44, md: 52 },
                      height: { xs: 44, md: 52 },
                      bgcolor: '#f5f3ff',
                      color: '#7c3aed',
                      border: '1px solid #ede9fe',
                      fontSize: 18,
                      fontWeight: 800,
                    }}
                    src="https://education.gsu.edu/files/2019/08/Headshot_Placeholder.jpg"
                  >
                    {order.patient.firstName?.charAt(0)}
                    {order.patient.lastName?.charAt(0)}
                  </Avatar>
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-purple-100 bg-white px-2 py-0.5 text-[10px] font-bold text-purple-700 shadow-sm">
                    Détails
                  </span>
                </button>

                <div className="min-w-0 flex-1 pt-0.5">
                  <h4 className="truncate text-sm font-bold leading-tight text-gray-950 md:text-base">
                    {order.patient.firstName} {order.patient.lastName}
                  </h4>
                  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px] font-semibold text-gray-500">
                    <span>{order.patient.birthDateStr || '-'}</span>
                    <span className="text-gray-300">•</span>
                    <span className="truncate">{order.patient.ssn || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setQuickPayDialogOpen(true)}
                  className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 text-left text-xs font-bold text-gray-800 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
                  title="Solde patient"
                >
                  <Wallet size={15} className="shrink-0" />
                  <span className="min-w-0 truncate">
                    {patientStats?.sold?.toFixed(2) ?? '—'} €
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsExamHistoryDialogOpen(true)}
                  className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 text-left text-xs font-bold text-gray-800 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
                  title="Historique des examens"
                >
                  <ClipboardPlus size={15} className="shrink-0" />
                  <span>{orderHistory?.totalCount ?? 0} examens</span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    patientStats?.noShowsCount && setIsNoShowDialogOpen(true)
                  }
                  className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 text-left text-xs font-bold text-gray-800 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700 disabled:cursor-default disabled:opacity-60 disabled:hover:border-gray-200 disabled:hover:bg-gray-50 disabled:hover:text-gray-800"
                  title="Rendez-vous manqués"
                  disabled={!patientStats?.noShowsCount}
                >
                  <Rabbit size={15} className="shrink-0" />
                  <span>{patientStats?.noShowsCount ?? 0} absences</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsTransportDialogOpen(true)}
                  className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 text-left text-xs font-bold text-gray-800 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
                  title="Transport"
                >
                  <Ambulance size={15} className="shrink-0" />
                  <span>Transport</span>
                </button>
              </div>
            </div>

            <Paper
              elevation={0}
              className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm"
            >
              <section className="rounded-md border border-gray-200 bg-gray-50 p-2">
                <OrderRecipientList order={order} />
              </section>

              <section className="rounded-md border border-gray-200 bg-gray-50 p-2">
                <Typography
                  variant="h6"
                  className="mb-2 font-bold text-gray-800"
                  sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
                >
                  Fichiers attachés
                </Typography>
                <DocumentsList
                  entityId={order.id}
                  entityName={EntityNameEnum.ORDER}
                  documents={order.documents ?? []}
                  setisEditingDocuments={setisEditingDocuments}
                  handleOnFileUploaded={handleOnFileUploaded}
                  isEditingDocuments={isEditingDocuments}
                  isLoading={isLoadingDocuments}
                  onDocumentDeleted={handleDocumentDeleted}
                  setSelectedDocument={setSelectedDocument}
                />
              </section>
            </Paper>
          </div>
        </Panel>
        <ResizeHandle horizontal className="" />
        <Panel order={2} defaultSize={75} minSize={40}>
          <div className="h-full min-h-0 flex flex-col overflow-hidden">
            <Paper
              elevation={0}
              className="h-full min-h-0 flex flex-col overflow-hidden rounded-xl bg-gray-50 p-3 sm:p-4"
            >
              <MedicalOrderForm
                order={effectiveOrder}
                editByDefault={editByDefault}
                onClose={onClose}
              />
              {order && order.medicalOrders.length !== 0 ? (
                <Quotation
                  order={order}
                  onMedicalOrderRemoved={onMedicalOrderRemoved}
                  toolbarContent={orderActionToolbar}
                />
              ) : (
                <div className="my-2 flex shrink-0 flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                  {orderActionToolbar}
                </div>
              )}
              <CommentsPopover
                order={order}
                createdByFullName={user?.firstName + ' ' + user?.lastName}
                anchorEl={commentsPopover.anchorEl}
                onClose={handleCloseComments}
                setOpenCommentDialog={setCommentDialogOpen}
                setEditingComment={setEditingComment}
                editingComment={editingComment}
                commentDialogOpen={commentDialogOpen}
                handleCloseComments={handleCloseComments}
              />
            </Paper>
          </div>
        </Panel>
      </PanelGroup>
      {order && (
        <SlideInPortal
          isOpen={isPatientInfoOpen}
          onClose={() => setIsPatientInfoOpen(false)}
          patientId={order.patient.id}
          visitId={order.visit?.id}
        />
      )}
      {order && (
        <DocumentListViewer
          isOpen={selectedDocument !== null}
          documents={order.documents ?? []}
          onClose={() => {
            setSelectedDocument(null)
          }}
          selectedDocument={selectedDocument || undefined}
          onSelectedDocumentChange={(doc) => {
            setSelectedDocument(doc)
          }}
        />
      )}
      <ExamSelectorDialog
        isOpen={isExamSelectorVisible}
        onClose={() => setIsExamSelectorVisible(false)}
        handleConfirm={handleExamSelectionChange}
        excludedExams={order.medicalOrders.map((mo) => mo.exam)}
      />
      <MissedAppointment
        isOpen={isNoShowDialogOpen}
        onClose={() => setIsNoShowDialogOpen(false)}
        noShows={patientStats?.noShows ?? []}
      />
      <PreferredExamsPopover
        anchorEl={examButtonAnchor}
        open={Boolean(examButtonAnchor)}
        onClose={() => setExamButtonAnchor(null)}
        onExamSelect={handleSingleExamSelect}
        onOpenFullSelector={() => setIsExamSelectorVisible(true)}
        loadingExamId={loadingExamId}
      />

      <UnpaidPatientInvoicesDialog
        quickPayOpen={quickPayDialogOpen}
        setQuickPayOpen={setQuickPayDialogOpen}
        quickPayPatientId={order?.patient.id}
      />

      <Dialog
        open={isTransportDialogOpen}
        onClose={() => setIsTransportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
          },
        }}
      >
        <DialogTitle
          title="Transport"
          onClose={() => setIsTransportDialogOpen(false)}
          format
        />
        <DialogContent>
          <TransportForm row={order} isOrder />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isExamHistoryDialogOpen}
        onClose={() => setIsExamHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          title="Historique des examens"
          onClose={() => setIsExamHistoryDialogOpen(false)}
          format
        />
        <DialogContent>
          <OrderHistoryComponent order={order} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsExamHistoryDialogOpen(false)}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default OrderDialog
